import React, { useCallback, useEffect, useId, useState } from "react";
import { Eye, EyeOff, KeyRound, ShieldCheck, Trash2, Trophy } from "lucide-react";
import api from "../../../services/api";
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  Loading,
  RoleBadge,
  Switch,
  UserStatusBadges,
  errorText,
  formatDate,
  formatDateTime,
  formatScore,
  fullName,
  initials,
  useToast,
} from "./common";

const EDITABLE = ["name", "surname", "username", "grade", "school", "role"];

const toForm = (u) => Object.fromEntries(EDITABLE.map((k) => [k, u?.[k] ?? (k === "role" ? "student" : "")]));

const ProfileForm = ({ user, onSaved }) => {
  const { notify } = useToast();
  const id = useId();
  const [form, setForm] = useState(() => toForm(user));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset only when the saved values change (not on every parent re-render)
  const signature = JSON.stringify(toForm(user));
  useEffect(() => {
    setForm(JSON.parse(signature));
  }, [signature]);

  const initial = toForm(user);
  const changes = Object.fromEntries(
    EDITABLE.filter((k) => String(form[k] ?? "").trim() !== String(initial[k] ?? "").trim()).map((k) => [
      k,
      typeof form[k] === "string" ? form[k].trim() : form[k],
    ])
  );
  const dirty = Object.keys(changes).length > 0;
  const invalid = !form.name.trim() || !form.username.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!dirty || invalid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/admin/users/${user.user_id}`, changes);
      onSaved(res.data);
      notify("Profile saved.");
    } catch (err) {
      setError(errorText(err, "Couldn't save those changes."));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = (k, label) => (
    <div className="eg-field">
      <label className="eg-label" htmlFor={`${id}-${k}`}>
        {label}
      </label>
      <input id={`${id}-${k}`} className="eg-input" value={form[k] ?? ""} onChange={set(k)} />
    </div>
  );

  return (
    <form className="admin-form" onSubmit={submit}>
      <div className="admin-form-grid">
        {input("name", "First name")}
        {input("surname", "Surname")}
        {input("username", "Username")}
        <div className="eg-field">
          <label className="eg-label" htmlFor={`${id}-role`}>
            Role
          </label>
          <select id={`${id}-role`} className="eg-input" value={form.role} onChange={set("role")}>
            <option value="student">Student</option>
            <option value="teacher">Teacher</option>
          </select>
        </div>
        {input("grade", "Grade")}
        {input("school", "School")}
      </div>
      {changes.role && (
        <p className="admin-field-hint">
          Changing the role moves this person to the {changes.role} dashboard the next time they sign in.
        </p>
      )}
      {invalid && <p className="admin-field-error">First name and username can't be empty.</p>}
      {error && <div className="admin-error admin-error-tight">{error}</div>}
      <div className="admin-form-actions">
        <button
          type="button"
          className="eg-btn eg-btn-outline"
          onClick={() => setForm(toForm(user))}
          disabled={!dirty || saving}
        >
          Reset
        </button>
        <button type="submit" className="eg-btn eg-btn-primary" disabled={!dirty || invalid || saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

const PasswordForm = ({ user }) => {
  const { notify } = useToast();
  const id = useId();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || saving) return;
    setSaving(true);
    setError(null);
    setDone(false);
    try {
      await api.post(`/admin/users/${user.user_id}/password`, { password });
      setPassword("");
      setDone(true);
      notify(`New password set for ${fullName(user)}.`);
    } catch (err) {
      setError(errorText(err, "Couldn't set the password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-form" onSubmit={submit}>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-pw`}>
          New password
        </label>
        <div className="admin-password">
          <input
            id={`${id}-pw`}
            className="eg-input"
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setDone(false);
            }}
            autoComplete="new-password"
            aria-describedby={`${id}-pw-hint`}
          />
          <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <small id={`${id}-pw-hint`} className="admin-field-hint">
          At least 8 characters. Share it with them securely and ask them to change it after signing in.
        </small>
      </div>
      {error && <div className="admin-error admin-error-tight">{error}</div>}
      {done && <div className="admin-success admin-error-tight">Password updated. Their old password no longer works.</div>}
      <div className="admin-form-actions">
        <button type="submit" className="eg-btn eg-btn-primary" disabled={password.length < 8 || saving}>
          <KeyRound size={16} /> {saving ? "Setting…" : "Set new password"}
        </button>
      </div>
    </form>
  );
};

const UserDrawer = ({ userId, onClose, onUpdated, onDeleted }) => {
  const { notify } = useToast();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setDetail(null);
    setError(null);
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setDetail(res.data);
    } catch (err) {
      setError(errorText(err, "Couldn't load this user."));
    }
  }, [userId]);

  useEffect(() => {
    setToggleError(null);
    setConfirmDelete(false);
    load();
  }, [load]);

  const applyUser = (updated) => {
    setDetail((d) => (d ? { ...d, user: { ...d.user, ...updated } } : d));
    onUpdated?.(updated);
  };

  const toggle = async (key, value) => {
    setToggling(key);
    setToggleError(null);
    try {
      const res = await api.patch(`/admin/users/${userId}`, { [key]: value });
      applyUser(res.data);
      const label =
        key === "is_verified"
          ? value
            ? "Account verified."
            : "Account marked unverified."
          : value
          ? "Account suspended. They can't sign in until you lift it."
          : "Suspension lifted.";
      notify(label);
    } catch (err) {
      setToggleError(errorText(err, "Couldn't update the account."));
    } finally {
      setToggling(null);
    }
  };

  const doDelete = async () => {
    const u = detail.user;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await api.delete(`/admin/users/${u.user_id}`, { params: { confirm: u.email } });
      setConfirmDelete(false);
      notify(`${fullName(u)} was deleted.`);
      onDeleted?.(u, res.data?.removed || {});
      onClose();
    } catch (err) {
      setDeleteError(errorText(err, "Couldn't delete this user."));
    } finally {
      setDeleting(false);
    }
  };

  const u = detail?.user;
  const s = detail?.stats || {};
  const isAdmin = u?.role === "admin";
  const results = Array.isArray(detail?.recent_results) ? detail.recent_results : [];

  return (
    <>
      <Dialog
        open={Boolean(userId)}
        onClose={onClose}
        variant="drawer"
        title={u ? fullName(u) : "User details"}
        subtitle={u?.email}
      >
        {error ? (
          <div className="admin-error admin-error-row">
            <span>{error}</span>
            <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
              Try again
            </button>
          </div>
        ) : !u ? (
          <Loading label="Loading user…" />
        ) : (
          <div className="admin-drawer-content">
            <div className="admin-profile-head">
              <span className="admin-avatar admin-avatar-lg">{initials(u)}</span>
              <div>
                <div className="admin-badges">
                  <RoleBadge role={u.role} />
                  {!isAdmin && <UserStatusBadges user={u} />}
                </div>
                <dl className="admin-meta">
                  <div><dt>Username</dt><dd>{u.username || "—"}</dd></div>
                  <div><dt>{u.role === "teacher" ? "Teacher ID" : "Student ID"}</dt><dd>{u.student_id || u.teacher_id || "—"}</dd></div>
                  <div><dt>Grade</dt><dd>{u.grade || "—"}</dd></div>
                  <div><dt>School</dt><dd>{u.school || "—"}</dd></div>
                  <div><dt>Joined</dt><dd>{formatDate(u.created_at)}</dd></div>
                  <div><dt>Last active</dt><dd>{formatDateTime(s.last_active || u.last_active)}</dd></div>
                </dl>
              </div>
            </div>

            <div className="admin-mini-stats">
              <div><strong>{s.quizzes_taken ?? 0}</strong><span>Quizzes taken</span></div>
              <div><strong>{formatScore(s.average_score)}</strong><span>Average score</span></div>
              <div><strong>{s.tutor_messages ?? 0}</strong><span>Tutor messages</span></div>
              <div><strong>{s.summaries ?? 0}</strong><span>Summaries</span></div>
              {u.points !== undefined && <div><strong>{u.points ?? 0}</strong><span>Points</span></div>}
              {u.level !== undefined && <div><strong>{u.level ?? "—"}</strong><span>Level</span></div>}
              {u.streak !== undefined && <div><strong>{u.streak ?? 0}</strong><span>Day streak</span></div>}
            </div>

            <section className="admin-drawer-section">
              <h4>Recent quiz results</h4>
              {results.length === 0 ? (
                <EmptyState icon={Trophy} title="No quiz results yet" />
              ) : (
                <ul className="admin-results">
                  {results.map((r, i) => (
                    <li key={`${r.at}-${i}`}>
                      <div>
                        <strong>{r.topic || r.subject || "Quiz"}</strong>
                        <small>
                          {[r.topic ? r.subject : null, formatDate(r.at)].filter(Boolean).join(" · ")}
                        </small>
                      </div>
                      <span className={`admin-score ${Number(r.percentage) >= 50 ? "good" : "low"}`}>
                        {formatScore(r.percentage)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {isAdmin ? (
              <div className="admin-notice">
                <ShieldCheck size={18} />
                <span>This is an admin account. Admin accounts can't be edited or deleted from the console.</span>
              </div>
            ) : (
              <>
                <section className="admin-drawer-section">
                  <h4>Account status</h4>
                  <div className="admin-toggle-row">
                    <div>
                      <strong>Verified</strong>
                      <small>Verified accounts can sign in without confirming their email.</small>
                    </div>
                    <Switch
                      checked={Boolean(u.is_verified)}
                      onChange={(v) => toggle("is_verified", v)}
                      disabled={toggling !== null}
                      label="Verified"
                    />
                  </div>
                  <div className="admin-toggle-row">
                    <div>
                      <strong>Suspended</strong>
                      <small>Suspended accounts can't sign in. Their data is kept.</small>
                    </div>
                    <Switch
                      checked={Boolean(u.is_suspended)}
                      onChange={(v) => toggle("is_suspended", v)}
                      disabled={toggling !== null}
                      label="Suspended"
                    />
                  </div>
                  {toggling && <p className="admin-field-hint">Saving…</p>}
                  {toggleError && <div className="admin-error admin-error-tight">{toggleError}</div>}
                </section>

                <section className="admin-drawer-section">
                  <h4>Edit profile</h4>
                  <ProfileForm user={u} onSaved={applyUser} />
                </section>

                <section className="admin-drawer-section">
                  <h4>Set new password</h4>
                  <PasswordForm user={u} />
                </section>

                <section className="admin-drawer-section admin-danger-zone">
                  <h4>Delete account</h4>
                  <p>
                    Permanently removes this account and the data linked to it, such as quiz results, tutor chats
                    and summaries. This can't be undone.
                  </p>
                  <button
                    type="button"
                    className="eg-btn admin-btn-danger"
                    onClick={() => {
                      setDeleteError(null);
                      setConfirmDelete(true);
                    }}
                  >
                    <Trash2 size={16} /> Delete user
                  </button>
                </section>
              </>
            )}
          </div>
        )}
      </Dialog>

      {u && (
        <ConfirmDialog
          open={confirmDelete}
          title={`Delete ${fullName(u)}?`}
          confirmLabel="Delete permanently"
          busy={deleting}
          error={deleteError}
          requireText={u.email}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>
            This permanently deletes <strong>{u.email}</strong> and all of their data. They won't be able to
            sign in again with this account.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};

export default UserDrawer;
